import {
  createApiRef,
  type DiscoveryApi,
  type FetchApi,
  type OAuthApi,
} from '@backstage/frontend-plugin-api';

/**
 * The portal's Keycloak sign-in (defined in packages/app, matched by id). The
 * demonstrator backend checks the user's Keycloak access token itself.
 */
export const oidcAuthApiRef = createApiRef<OAuthApi>({
  id: 'auth.oidc',
});

export type AgenticRole = 'bearbeitung' | 'freigabe' | 'admin';

export interface AgenticAccess {
  allowed: boolean;
  roles: AgenticRole[];
}

/** Error of the demonstrator backend, with the message the stand-alone UI shows. */
export class AgenticError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
    this.name = 'AgenticError';
  }
}

export interface AgenticApi {
  getAccess(): Promise<AgenticAccess>;
  /** Calls the demonstrator backend, e.g. `request('/v0/cases')`. */
  request<T>(path: string, init?: RequestInit): Promise<T>;
}

export const agenticApiRef = createApiRef<AgenticApi>({
  id: 'plugin.visionflow.agentic',
});

type Detail = { sheet?: string; row?: number; message?: string; code?: string };

/** Error text as the stand-alone demonstrator UI shows it. */
function errorText(status: number, payload: any) {
  const details = payload?.error?.details;
  if (
    payload?.error?.code === 'WORKBOOK_VALIDATION_ERROR' &&
    Array.isArray(details) &&
    details.length
  ) {
    const issues = (details as Detail[])
      .slice(0, 5)
      .map(issue =>
        [
          issue.sheet,
          issue.row ? `Zeile ${issue.row}` : null,
          issue.message || issue.code,
        ]
          .filter(Boolean)
          .join(', '),
      );
    return `Arbeitsmappe ungültig: ${issues.join('; ')}${
      details.length > 5 ? ` (+${details.length - 5} weitere)` : ''
    }`;
  }
  if (status === 401) {
    return 'Ihre Sitzung ist abgelaufen oder ungültig. Bitte laden Sie die Seite neu.';
  }
  return (
    payload?.error?.message ||
    payload?.message ||
    (Array.isArray(details) ? details.join(', ') : '') ||
    `HTTP ${status}`
  );
}

export class AgenticClient implements AgenticApi {
  constructor(
    private readonly deps: {
      discoveryApi: DiscoveryApi;
      fetchApi: FetchApi;
      oidcAuthApi: OAuthApi;
    },
  ) {}

  private async base() {
    return `${await this.deps.discoveryApi.getBaseUrl('visionflow')}/agentic`;
  }

  async getAccess(): Promise<AgenticAccess> {
    const response = await this.deps.fetchApi.fetch(
      `${await this.base()}/access`,
    );
    if (!response.ok)
      throw new AgenticError(`HTTP ${response.status}`, response.status);
    return response.json();
  }

  async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const headers = new Headers(init.headers);
    const form = init.body instanceof FormData;
    if (init.body && !form) headers.set('content-type', 'application/json');
    // fetchApi adds the Backstage token; the Keycloak token travels separately.
    headers.set('x-oidc-token', await this.deps.oidcAuthApi.getAccessToken());
    const response = await this.deps.fetchApi.fetch(
      `${await this.base()}/api${path}`,
      { ...init, headers },
    );
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new AgenticError(
        errorText(response.status, payload),
        response.status,
      );
    }
    return payload as T;
  }
}
